import { defineComponent, h, reactive } from 'vue';

const defaultOptions = {
  canSuccess: true,
  show: false,
  color: '#73ccec',
  position: 'fixed',
  failedColor: 'red',
  thickness: '2px',
  transition: {
    speed: '0.2s',
    opacity: '0.6s',
    termination: 300,
  },
  autoRevert: true,
  location: 'top',
  inverse: false,
  autoFinish: true,
};

function createState(options) {
  return reactive({
    percent: 0,
    options: {
      ...defaultOptions,
      ...options,
      transition: {
        ...defaultOptions.transition,
        ...(options.transition ?? {}),
      },
    },
  });
}

function createStyle(state) {
  const options = state.options;
  const style = {
    'background-color': options.canSuccess ? options.color : options.failedColor,
    opacity: options.show ? 1 : 0,
    position: options.position,
    'z-index': 999999,
  };

  if (options.location === 'top' || options.location === 'bottom') {
    style[options.location] = '0px';
    style[options.inverse ? 'right' : 'left'] = '0px';
    style.width = `${state.percent}%`;
    style.height = options.thickness;
    style.transition = `${options.show ? `width ${options.transition.speed}, ` : ''}opacity ${options.transition.opacity}`;
  } else {
    style[options.location] = '0px';
    style[options.inverse ? 'top' : 'bottom'] = '0px';
    style.height = `${state.percent}%`;
    style.width = options.thickness;
    style.transition = `${options.show ? `height ${options.transition.speed}, ` : ''}opacity ${options.transition.opacity}`;
  }

  return style;
}

export default {
  install(app, options = {}) {
    const state = createState(options);
    const temp = {};
    let timer = null;

    const progress = {
      start(duration = 3000) {
        state.percent = 0;
        state.options.show = true;
        state.options.canSuccess = true;
        const cut = 10000 / Math.floor(duration);
        clearInterval(timer);
        timer = setInterval(() => {
          progress.increase(cut * Math.random());
          if (state.percent > 95 && state.options.autoFinish) {
            progress.finish();
          }
        }, 100);
      },
      set(value) {
        state.options.show = true;
        state.options.canSuccess = true;
        state.percent = Math.floor(value);
      },
      get() {
        return Math.floor(state.percent);
      },
      increase(value) {
        state.percent = Math.min(99, state.percent + Math.floor(value));
      },
      decrease(value) {
        state.percent -= Math.floor(value);
      },
      hide() {
        clearInterval(timer);
        timer = null;
        setTimeout(() => {
          state.options.show = false;
          setTimeout(() => {
            state.percent = 0;
          }, 100);
          if (state.options.autoRevert) {
            setTimeout(() => progress.revert(), 300);
          }
        }, state.options.transition.termination);
      },
      pause() {
        clearInterval(timer);
      },
      finish() {
        state.percent = 100;
        progress.hide();
      },
      fail() {
        state.options.canSuccess = false;
        state.percent = 100;
        progress.hide();
      },
      setFailColor(value) {
        state.options.failedColor = value;
      },
      setColor(value) {
        state.options.color = value;
      },
      setLocation(value) {
        state.options.location = value;
      },
      setTransition(value) {
        state.options.transition = value;
      },
      tempFailColor(value) {
        temp.failedColor = state.options.failedColor;
        state.options.failedColor = value;
      },
      tempColor(value) {
        temp.color = state.options.color;
        state.options.color = value;
      },
      tempLocation(value) {
        temp.location = state.options.location;
        state.options.location = value;
      },
      tempTransition(value) {
        temp.transition = state.options.transition;
        state.options.transition = value;
      },
      revertColor() {
        state.options.color = temp.color;
        temp.color = null;
      },
      revertFailColor() {
        state.options.failedColor = temp.failedColor;
        temp.failedColor = null;
      },
      revertLocation() {
        state.options.location = temp.location;
        temp.location = null;
      },
      revertTransition() {
        state.options.transition = temp.transition;
        temp.transition = null;
      },
      revert() {
        if (!state.options.autoRevert) return;
        if (temp.color) progress.revertColor();
        if (temp.failedColor) progress.revertFailColor();
        if (temp.location) progress.revertLocation();
        if (temp.transition) progress.revertTransition();
      },
      parseMeta(meta) {
        if (!meta?.func) return;
        Object.values(meta.func).forEach((item) => {
          if (item.call === 'color' && item.modifier === 'set') progress.setColor(item.argument);
          if (item.call === 'color' && item.modifier === 'temp') progress.tempColor(item.argument);
          if (item.call === 'fail' && item.modifier === 'set') progress.setFailColor(item.argument);
          if (item.call === 'fail' && item.modifier === 'temp') progress.tempFailColor(item.argument);
          if (item.call === 'location' && item.modifier === 'set') progress.setLocation(item.argument);
          if (item.call === 'location' && item.modifier === 'temp') progress.tempLocation(item.argument);
          if (item.call === 'transition' && item.modifier === 'set') progress.setTransition(item.argument);
          if (item.call === 'transition' && item.modifier === 'temp') progress.tempTransition(item.argument);
        });
      },
    };

    app.config.globalProperties.$Progress = progress;
    app.component('vue-progress-bar', defineComponent({
      name: 'VueProgressBar',
      setup() {
        return () => h('div', {
          class: '__cov-progress',
          style: createStyle(state),
        });
      },
    }));
  },
};
